import React from 'react';
import { Bell, ThumbsUp, ThumbsDown, Reply, Smile, Check, CheckCheck, MessageCircle, Heart, Camera, ImagePlus, Users, CalendarHeart, CalendarCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import {
  Notification,
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../lib/notification-service';

interface NotificationBellProps {
  onOpenMessageCenter?: (threadCode?: string, threadType?: 'country' | 'general' | 'bug') => void;
}

export function NotificationBell({ onOpenMessageCenter }: NotificationBellProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const unreadCount = React.useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  React.useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeToNotifications(user.uid, setNotifications);
    return () => unsub();
  }, [user?.uid]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markNotificationRead(notification.id);
    }
    setIsOpen(false);
    const isMediaNotification = notification.type === 'media_like' || notification.type === 'media_comment' || notification.type === 'media_comment_reply' || notification.type === 'media_new_post';
    const isActivityNotification = notification.type === 'activity_join' || notification.type === 'activity_like' || notification.type === 'activity_comment' || notification.type === 'activity_reply';
    if (isMediaNotification) {
      navigate('/media');
    } else if (isActivityNotification) {
      navigate('/activites');
    } else if (notification.type === 'pvtmate_new_listing' && notification.pvtmate_id) {
      navigate(`/matching/${notification.pvtmate_id}`);
    } else if ((notification.type === 'profile_comment' || notification.type === 'profile_like') && notification.profile_id) {
      navigate(`/matching/${notification.profile_id}`);
    } else {
      onOpenMessageCenter?.(notification.thread_code, notification.thread_type as 'country' | 'general' | 'bug');
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.uid) return;
    await markAllNotificationsRead(user.uid, notifications);
  };

  const recentNotifications = notifications.slice(0, 30);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-xl transition-all duration-200"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-900 animate-scale-in">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 w-auto sm:w-96 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden z-[100] animate-scale-in">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {recentNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-3">
                  <Bell className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Aucune notification
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Vous serez notifie des reactions et reponses
                </p>
              </div>
            ) : (
              recentNotifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationItem({
  notification,
  onClick,
}: {
  notification: Notification;
  onClick: () => void;
}) {
  const icon = React.useMemo(() => {
    switch (notification.type) {
      case 'like':
        return <ThumbsUp className="w-3.5 h-3.5 text-emerald-500" />;
      case 'dislike':
        return <ThumbsDown className="w-3.5 h-3.5 text-red-400" />;
      case 'emoji_reaction':
        return notification.emoji ? (
          <span className="text-sm">{notification.emoji}</span>
        ) : (
          <Smile className="w-3.5 h-3.5 text-amber-500" />
        );
      case 'reply':
        return <Reply className="w-3.5 h-3.5 text-blue-500" />;
      case 'profile_comment':
        return <MessageCircle className="w-3.5 h-3.5 text-teal-500" />;
      case 'profile_like':
        return <Heart className="w-3.5 h-3.5 text-pink-500 fill-pink-500" />;
      case 'media_like':
        return <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" />;
      case 'media_comment':
        return <MessageCircle className="w-3.5 h-3.5 text-blue-500" />;
      case 'media_comment_reply':
        return <Reply className="w-3.5 h-3.5 text-cyan-500" />;
      case 'media_new_post':
        return <ImagePlus className="w-3.5 h-3.5 text-emerald-500" />;
      case 'pvtmate_new_listing':
        return <Users className="w-3.5 h-3.5 text-blue-500" />;
      case 'activity_join':
        return <CalendarCheck className="w-3.5 h-3.5 text-teal-500" />;
      case 'activity_like':
        return <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500" />;
      case 'activity_comment':
        return <MessageCircle className="w-3.5 h-3.5 text-teal-500" />;
      case 'activity_reply':
        return <Reply className="w-3.5 h-3.5 text-teal-500" />;
      default:
        return <Bell className="w-3.5 h-3.5 text-gray-400" />;
    }
  }, [notification.type, notification.emoji]);

  const label = React.useMemo(() => {
    switch (notification.type) {
      case 'like':
        return 'a aime votre message';
      case 'dislike':
        return 'n\'a pas aime votre message';
      case 'emoji_reaction':
        return `a reagi ${notification.emoji || ''} a votre message`;
      case 'reply':
        return 'a repondu a votre message';
      case 'profile_comment':
        return 'a commente votre annonce PVTmates';
      case 'profile_like':
        return 'a ajoute votre annonce PVTmates en favori';
      case 'media_like':
        return 'a aime votre photo';
      case 'media_comment':
        return 'a commente votre photo';
      case 'media_comment_reply':
        return 'a aussi commente une photo';
      case 'media_new_post':
        return 'a publie une nouvelle photo';
      case 'pvtmate_new_listing':
        return 'cherche un PVTmate';
      case 'activity_join':
        return 'a rejoint votre activite';
      case 'activity_like':
        return 'a aime votre activite';
      case 'activity_comment':
        return 'a commente votre activite';
      case 'activity_reply':
        return 'a repondu sur votre activite';
      default:
        return '';
    }
  }, [notification.type, notification.emoji]);

  const timeAgo = React.useMemo(() => {
    try {
      return formatDistanceToNow(notification.created_at.toDate(), {
        addSuffix: true,
        locale: fr,
      }).replace('environ ', '');
    } catch {
      return '';
    }
  }, [notification.created_at]);

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
        !notification.read
          ? 'bg-blue-50/50 dark:bg-blue-900/10'
          : ''
      }`}
    >
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] leading-snug text-gray-800 dark:text-gray-200">
          <span className="font-bold">{notification.sender_username}</span>{' '}
          <span className="text-gray-600 dark:text-gray-400">{label}</span>
        </p>
        {notification.message_preview && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            "{notification.message_preview}"
          </p>
        )}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">{timeAgo}</p>
      </div>
      {!notification.read && (
        <div className="flex-shrink-0 mt-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full" />
        </div>
      )}
    </button>
  );
}
